/* If you edit this file, please remove this header and clean up the resulting eslint errors.
 */
/* eslint-disable
  import/no-extraneous-dependencies,
  no-alert,
  no-console,
  react/jsx-no-target-blank,
  react/no-unescaped-entities,
  react/prop-types,
  no-restricted-syntax
*/
import React from 'react';
import PropTypes from 'prop-types';

import Code from './components/code';
import Form from './components/form';
import EventLog from './components/event-log';
import { withAppContext, withAppContextProvider } from './contexts/app';
import eventCapturingClient from './client-with-logging';

import './index.scss';

class EmbeddedTestingTool extends React.PureComponent {
  static propTypes = {
    getSampleURL: PropTypes.string.isRequired,
    csrfToken: PropTypes.string.isRequired,
    cultures: PropTypes.object.isRequired,
    isProduction: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      eventLoggingEnabled: false,
      regularClient: null,
    };
  }

  toggleEventLogging = () => {
    this.setState({ eventLoggingEnabled: !this.state.eventLoggingEnabled });
  };

  launchEmbedded = async () => {
    // Use event capturing client if enabled, otherwise dynamically import regular client
    let client;
    if (this.state.eventLoggingEnabled) {
      client = eventCapturingClient;
    } else if (!this.state.regularClient) {
      const { default: regularClient } = await import('./client');
      this.setState({ regularClient });
      client = regularClient;
    } else {
      client = this.state.regularClient;
    }
    const { form, isSampleDocument, hasLaunchedSampleDocument } =
      this.props.appContext.state;

    if (!hasLaunchedSampleDocument && isSampleDocument) {
      this.props.appContext.actions.launchSampleEmbeddedDocument();
    }

    const opts = {
      allowCancel: form.allowCancel,
      clientId: form.clientID,
      hideHeader: form.hideHeader,
      locale: form.locale,
      testMode: form.testMode,
      timeout: form.timeout,
    };

    if (form.redirectURL.length) {
      opts.redirectTo = form.redirectURL;
    }

    if (form.requestingEmail.length) {
      opts.requestingEmail = form.requestingEmail;
    }

    if (form.whiteLabelingCode.length) {
      try {
        opts.whiteLabeling = JSON.parse(form.whiteLabelingCode);
      } catch (err) {
        // Not valid JSON.
      }
    }

    client.once('close', () => {
      this.props.appContext.actions.resetEmbedded();
    });

    client.open(form.signURL, opts);
  };

  componentDidMount() {
    const {
      csrfToken,
      cultures,
      getSampleURL,
      requestClientId,
      requestEmbeddedUrl,
      launchOnLoad,
    } = this.props;
    this.props.appContext.actions.hydrateStore({
      getSampleURL,
      csrfToken,
      cultures,
      requestClientId,
      requestEmbeddedUrl,
      launchOnLoad,
    });
    if (requestClientId && requestEmbeddedUrl) {
      const decodedEmbeddedUrl = decodeURIComponent(requestEmbeddedUrl);
      this.props.appContext.actions.updateFormOptions({
        clientID: requestClientId,
        signURL: decodedEmbeddedUrl,
      });
    }
    if (launchOnLoad) {
      this.launchEmbedded();
    }
  }

  render() {
    return (
      <div className="embedded-testing-tool clearfix">
        <div className="m-api-docs-section">
          <p className="m-api-docs-note l-margin-b-20">
            <strong className="m-api-docs-important">NOTE:</strong> This testing
            tool uses HelloSign Embedded v2. As of <strong>August 2020</strong>,
            HelloSign Embedded v1 is no longer officially supported. You can
            access a previous version of this testing tool{' '}
            <a href="/api/embeddedTestV1">here</a>.
          </p>
          <p>
            You can use this page to quickly test any of HelloSign's Embedded
            flows without having to write a single line of JavaScript.
            Submitting the form below will trigger HelloSign Embedded's{' '}
            <code>open()</code> method with the options you specify.
          </p>
          <p>
            If you already have a client ID and embedded URL, enter them in the
            fields below. Or if this is your first time, we can generate this
            data for you by pressing the blue &ldquo;Get a sample
            document&rdquo; button.
          </p>
        </div>
        <Form launchEmbedded={this.launchEmbedded} />
        <Code launchEmbedded={this.launchEmbedded} />
        {!this.props.isProduction && (
          <div style={{ clear: 'both' }}>
            <div className="event-logging-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={this.state.eventLoggingEnabled}
                  onChange={this.toggleEventLogging}
                  data-testid="enabled-event-logging"
                />
                <span style={{ marginLeft: '8px' }}>
                  Enable Event Logging
                  <small style={{ color: '#666', marginLeft: '8px' }}>
                    (Captures HelloSign embedded events for debugging)
                  </small>
                </span>
              </label>
            </div>
            {this.state.eventLoggingEnabled && (
              <EventLog client={eventCapturingClient} />
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withAppContextProvider(withAppContext(EmbeddedTestingTool));
